/**
 * Resilience helpers for talking to the (currently test-only) mdm-user-settings
 * service. The service may be slow or entirely unreachable, so every call is
 * bounded by a timeout and failures are turned into a typed error the caller can
 * treat as "service unavailable" and fall back to the legacy blob.
 */

/** Default per-request timeout. Short: the blob is always available as a fallback. */
export const DEFAULT_SETTINGS_TIMEOUT_MS = 3500;

/** Thrown when the settings service times out or is otherwise unreachable. */
export class ServiceUnavailableError extends Error {
  constructor(message: string, options?: { cause?: unknown }) {
    super(message);
    this.name = 'ServiceUnavailableError';
    if (options?.cause !== undefined) {
      (this as { cause?: unknown }).cause = options.cause;
    }
  }
}

/**
 * `fetch` with an AbortController-based timeout. A timeout or network error is
 * normalised to a {@link ServiceUnavailableError}; HTTP status handling is left
 * to the caller (inspect `res.ok`).
 */
export const fetchWithTimeout = async (
  input: RequestInfo | URL,
  init: RequestInit = {},
  timeoutMs: number = DEFAULT_SETTINGS_TIMEOUT_MS,
): Promise<Response> => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(input, { ...init, signal: controller.signal });
  } catch (error) {
    if (controller.signal.aborted) {
      throw new ServiceUnavailableError(
        `Settings service request timed out after ${timeoutMs}ms`,
        { cause: error },
      );
    }
    throw new ServiceUnavailableError('Settings service request failed', {
      cause: error,
    });
  } finally {
    clearTimeout(timer);
  }
};

/**
 * Circuit breaker for the (test-only) settings service.
 *
 * The service may be down, slow, or unreachable for long stretches. Without a
 * brake, any code path that reacts to a failure (a retrying query, a login
 * import that re-seeds on every blob change) can hammer it with thousands of
 * doomed requests. After {@link BREAKER_FAILURE_THRESHOLD} consecutive
 * "service-unavailable" signals (timeouts, network errors, 5xx) the circuit
 * **opens** for {@link BREAKER_COOLDOWN_MS}: during the cooldown every call
 * fails fast with a {@link ServiceUnavailableError} without touching the
 * network. Any reachable response (even a 4xx — the service is clearly up)
 * closes it again.
 *
 * State is module-level on purpose: it is a property of the single downstream
 * service, shared across every hook/consumer, and must survive re-renders.
 */
export const BREAKER_FAILURE_THRESHOLD = 3;
export const BREAKER_COOLDOWN_MS = 30_000;

const breaker = { failures: 0, openUntil: 0 };

/** True while the circuit is open (calls should fail fast). */
export const isCircuitOpen = (now: number = Date.now()): boolean =>
  now < breaker.openUntil;

/** Test-only: reset the breaker to its closed, zero-failure state. */
export const resetCircuitBreaker = (): void => {
  breaker.failures = 0;
  breaker.openUntil = 0;
};

const recordReachable = (): void => {
  breaker.failures = 0;
  breaker.openUntil = 0;
};

const recordUnavailable = (now: number = Date.now()): void => {
  breaker.failures += 1;
  if (breaker.failures >= BREAKER_FAILURE_THRESHOLD) {
    breaker.openUntil = now + BREAKER_COOLDOWN_MS;
  }
};

/**
 * The single entry point every settings request goes through. It wraps
 * {@link fetchWithTimeout} with the circuit breaker so a failing service is
 * throttled automatically:
 *
 * - Circuit open → throw immediately, no request.
 * - Reachable (`ok` or any `< 500`) → close the breaker, return the response.
 * - `>= 500` → count a failure, still return the response (callers inspect
 *   `res.ok` and raise their own typed error).
 * - Timeout / network error → count a failure and rethrow.
 */
export const settingsFetch = async (
  input: RequestInfo | URL,
  init: RequestInit = {},
  timeoutMs: number = DEFAULT_SETTINGS_TIMEOUT_MS,
): Promise<Response> => {
  if (isCircuitOpen()) {
    throw new ServiceUnavailableError(
      'Settings service circuit is open; skipping request',
    );
  }
  try {
    const res = await fetchWithTimeout(input, init, timeoutMs);
    if (res.status < 500) recordReachable();
    else recordUnavailable();
    return res;
  } catch (error) {
    recordUnavailable();
    throw error;
  }
};

/**
 * Run a promise for its side effect without letting a rejection propagate — used
 * for writes to the service that must never block or fail the user's action.
 */
export const bestEffort = async <T>(
  promise: Promise<T>,
  onError?: (error: unknown) => void,
): Promise<T | undefined> => {
  try {
    return await promise;
  } catch (error) {
    onError?.(error);
    return undefined;
  }
};
